<template>
	<nav
		:class="['markets-anchor-menu']"
		:style="menuStyle"
	>
		<div class="markets-anchor-menu-container">
			<a
				v-for="item in menu"
				:key="item.label"
				:href="item.href"
				class="block px-4 py-3 font-regular transition markets-anchor-menu-item"
				:class="[
					'text-left',
					{
						'featured-anchor': active === item.label,
						'show-tooltip': ['#coverage', '#deep-dives'].includes(item.href)
					}
				]"
				@click="scrollToAnchor(item.href, item.label)"
			>
				<span class="anchor-tooltip">{{ item.label }}</span>
			</a>
		</div>
	</nav>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";

const menu = [
	{ label: "Summary", href: "#summary" },
	{ label: "News", href: "#news" },
	{ label: "Financials", href: "#financials" },
	{ label: "Analyst Rating", href: "#analyst-rating" },
	{ label: "Earnings", href: "#earning" },
	{ label: "FAQs", href: "#faqs" }
];

const active = ref(menu[0].label);
const left = ref("0px");

const breakpoints = useBreakpoints({
	xl: 1690,
	midxl: 1440,
	lg: 1280
});

const isWide = computed(() => breakpoints.isGreater("xl"));
const isMidXl = computed(() => breakpoints.isGreater("midxl"));

function setActive(label) {
	active.value = label;
}

const menuStyle = computed(() => ({
	top: isMidXl.value ? "112px" : "60px",
	left: left.value
}));

function updateLeft() {
	if (!isWide.value) {
		left.value = "0px";
		return;
	}
	const container = document.querySelector(`#quote-page-content`);
	if (container) {
		const rect = container.getBoundingClientRect();
		left.value = rect.left + (window.scrollX - 162) + "px";
	} else {
		left.value = "0px";
	}
}

function scrollToAnchor(href, label) {
	setActive(label);
	const id = href.replace("#", "");
	const el = document.getElementById(id);
	if (el) {
		el.scrollIntoView({ behavior: "smooth" });
		history.replaceState(null, "", href);
	}
}

function onScroll() {
	const offset = 120; // adjust to your sticky header height
	let bestMatchIdx = -1;
	let maxVisibility = 0;

	for (let i = 0; i < menu.length; i++) {
		const id = menu[i].href.replace("#", "");
		const el = document.getElementById(id);
		if (el) {
			const rect = el.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			// Calculate how much of the section is visible in the viewport
			const sectionTop = rect.top;
			const sectionBottom = rect.bottom;
			const viewportTop = offset;
			const viewportBottom = viewportHeight;

			// Calculate the visible portion of the section
			const visibleTop = Math.max(sectionTop, viewportTop);
			const visibleBottom = Math.min(sectionBottom, viewportBottom);
			const visibleHeight = Math.max(0, visibleBottom - visibleTop);

			// Calculate visibility percentage
			const sectionHeight = rect.height;
			const visibilityPercentage =
				sectionHeight > 0 ? visibleHeight / sectionHeight : 0;

			// Also consider distance from top for tie-breaking
			const distanceFromTop = Math.abs(sectionTop - viewportTop);

			// Score based on visibility and proximity to top
			const score =
				visibilityPercentage - (distanceFromTop / viewportHeight) * 0.1;

			if (score > maxVisibility) {
				maxVisibility = score;
				bestMatchIdx = i;
			}
		}
	}

	// If no section has good visibility, find the closest one to the top
	if (bestMatchIdx === -1 || maxVisibility < 0.1) {
		let closestIdx = -1;
		let minDist = Infinity;

		for (let i = 0; i < menu.length; i++) {
			const id = menu[i].href.replace("#", "");
			const el = document.getElementById(id);
			if (el) {
				const rect = el.getBoundingClientRect();
				const dist = Math.abs(rect.top - offset);
				if (dist < minDist) {
					minDist = dist;
					closestIdx = i;
				}
			}
		}
		bestMatchIdx = closestIdx;
	}

	// Fallback to first item if nothing found
	if (bestMatchIdx === -1) {
		bestMatchIdx = 0;
	}

	setActive(menu[bestMatchIdx].label);
}

let scrollTimeout;
function debouncedOnScroll() {
	clearTimeout(scrollTimeout);
	scrollTimeout = setTimeout(onScroll, 10);
}

onMounted(() => {
	if (isWide.value) {
		updateLeft();
		window.addEventListener("resize", updateLeft);
		window.addEventListener("scroll", updateLeft);
	}
	window.addEventListener("scroll", debouncedOnScroll, { passive: true });
});

onBeforeUnmount(() => {
	window.removeEventListener("resize", updateLeft);
	window.removeEventListener("scroll", updateLeft);
	window.removeEventListener("scroll", debouncedOnScroll);
});

watch(isWide, (val) => {
	if (val) {
		updateLeft();
		window.addEventListener("resize", updateLeft);
		window.addEventListener("scroll", updateLeft);
	} else {
		window.removeEventListener("resize", updateLeft);
		window.removeEventListener("scroll", updateLeft);
	}
});
</script>

<style scoped lang="scss">
@use "~/assets/utils" as *;
.markets-anchor-menu {
	position: relative;
	position: sticky;
	top: 0;
	z-index: 20;
	overflow-x: hidden;
	width: 100%;
	@include breakpoint(midxl) {
		left: -20px;

		width: 144px;
		position: fixed;
		right: 0;
		padding-left: 0;
		padding-right: 0;
		margin-right: 0;
		z-index: 1;
	}
}

.markets-anchor-menu-container {
	display: flex;
	flex-direction: row;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
	padding: 4px 4px;
	width: 100%;
	&::-webkit-scrollbar {
		display: none;
	}
	scrollbar-width: none;
	line-height: 1;
	background-color: #eff6ff;
	@include breakpoint(lg) {
		padding: 12px 16px;

		line-height: normal;
	}

	@include breakpoint(midxl) {
		flex-direction: column;
		overflow-x: visible;
		padding: 0;
		scroll-snap-type: x mandatory;
	}
}

.markets-anchor-menu-item {
	border: none;
	box-shadow: none;
	color: $black;
	white-space: nowrap;
	padding: 4px 16px;
	min-height: 40px;
	display: flex;
	align-items: center;
	font-weight: 500;
	@include breakpoint(lg) {
		padding: 10px 16px;
		scroll-snap-align: start;
		min-height: 44px;
		max-width: 152px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&:hover,
	&:active,
	&:target {
		@extend .featured-anchor;
	}
}

.featured-anchor {
	position: relative;
	display: block;
	text-align: left;
	padding: 10px 16px;
	background: rgba($blue-300, 0.14);
	z-index: 1;
	transition: all 0.15s ease-in-out;
	padding: 4px 16px;
	min-height: 40px;
	display: flex;
	align-items: center;
	color: $blue-300 !important;
	border-radius: 4px;

	@include breakpoint(lg) {
		padding: 10px 16px;
		min-height: 44px;
	}

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		z-index: -1;
		background: rgba($blue-300, 0.14);
	}
}

[id] {
	scroll-margin-top: 124px;
}
</style>
